<template>
  <div class="requests-table-control">
    <!-- Tabs -->
    <div class="tabs-row">
      <button
        :class="['tab-btn', { 'tab-btn-active': activeTab === 'in_turn' }]"
        @click="activeTab = 'in_turn'"
      >
        <feather-icon
          icon="RefreshCwIcon"
          size="18"
        />
        <span>In Turn</span>
        <span class="tab-badge">{{ inTurnCount }}</span>
      </button>
      <button
        :class="['tab-btn', { 'tab-btn-active': activeTab === 'pending' }]"
        @click="activeTab = 'pending'"
      >
        <feather-icon
          icon="ClockIcon"
          size="18"
        />
        <span>Pending</span>
        <span class="tab-badge">{{ pendingCount }}</span>
      </button>
    </div>

    <!-- Filters -->
    <div class="filters-row">
      <div class="filter-group">
        <feather-icon
          icon="LayersIcon"
          size="16"
          class="filter-icon"
        />
        <span class="filter-label">Source</span>
        <div class="filter-checkboxes">
          <label
            v-for="opt in availableSources"
            :key="opt.value"
            class="filter-checkbox"
          >
            <input
              v-model="selectedSources"
              type="checkbox"
              :value="opt.value"
            >
            <span>{{ opt.text }}</span>
          </label>
        </div>
      </div>
      <div class="filter-group filter-search">
        <feather-icon
          icon="SearchIcon"
          size="16"
          class="filter-icon"
        />
        <input
          v-model="searchQuery"
          type="text"
          class="search-input"
          placeholder="Search by request code..."
        >
      </div>
    </div>

    <!-- Table -->
    <div class="table-wrapper">
      <div
        v-if="filteredItems.length === 0"
        class="empty-state"
      >
        <feather-icon
          icon="InboxIcon"
          size="40"
        />
        <p>No requests found</p>
      </div>
      <div
        v-else
        class="table-scroll"
      >
        <table class="data-table">
          <thead>
            <tr>
              <th>
                <span class="th-content">
                  <feather-icon
                    icon="HashIcon"
                    size="14"
                  />
                  Request Code
                </span>
              </th>
              <th>
                <span class="th-content">
                  <feather-icon
                    icon="ActivityIcon"
                    size="14"
                  />
                  Status
                </span>
              </th>
              <th>
                <span class="th-content">
                  <feather-icon
                    icon="TagIcon"
                    size="14"
                  />
                  Internal Status
                </span>
              </th>
              <th>
                <span class="th-content">
                  <feather-icon
                    icon="CreditCardIcon"
                    size="14"
                  />
                  Payment
                </span>
              </th>
              <th>
                <span class="th-content">
                  <feather-icon
                    icon="CalendarIcon"
                    size="14"
                  />
                  Source Date
                </span>
              </th>
              <th>
                <span class="th-content">
                  <feather-icon
                    icon="GlobeIcon"
                    size="14"
                  />
                  Source
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="item in filteredItems"
              :key="item.id"
              class="table-row"
            >
              <td>
                <a
                  :href="`${BaseUrl.linkToEngineers}/${item.id}?role=${accessRole}&name=${accessName}&email=${accessEmail}&token=${accessToken}`"
                  target="_blank"
                  class="request-link"
                >
                  <feather-icon
                    icon="ExternalLinkIcon"
                    size="14"
                  />
                  {{ item.request_code }}
                </a>
              </td>
              <td>
                <span class="status-badge status-status">
                  {{ item.status }}
                </span>
              </td>
              <td>
                <span class="status-badge status-internal">
                  {{ item.enternal_status }}
                </span>
              </td>
              <td>
                <span
                  :class="['payment-badge', item.payment_status === 'paid' ? 'payment-paid' : 'payment-pending']"
                >
                  <feather-icon
                    :icon="item.payment_status === 'paid' ? 'CheckCircleIcon' : 'ClockIcon'"
                    size="12"
                  />
                  {{ item.payment_status }}
                </span>
              </td>
              <td>{{ item.source_date }}</td>
              <td>
                <span class="source-badge">
                  {{ item.source }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import BaseUrl from '@/libs/base-url'

export default {
  props: {
    dataTable: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      activeTab: 'in_turn',
      selectedSources: [],
      searchQuery: '',
      accessRole: localStorage.getItem('accessRole') || '',
      accessToken: localStorage.getItem('accessToken') || '',
      accessName: localStorage.getItem('name') || '',
      accessEmail: localStorage.getItem('email') || '',
      BaseUrl,
    }
  },
  computed: {
    inTurnCount() {
      return this.dataTable?.in_turn_list
        ? Object.values(this.dataTable.in_turn_list).flat().length
        : 0
    },
    pendingCount() {
      return this.dataTable?.pending_list
        ? Object.values(this.dataTable.pending_list).flat().length
        : 0
    },
    mergedData() {
      const currentTabData = this.activeTab === 'in_turn'
        ? this.dataTable?.in_turn_list
        : this.dataTable?.pending_list
      if (!currentTabData) return []
      return Object.values(currentTabData).flat()
    },
    availableSources() {
      const sourceCountMap = {}

      this.mergedData.forEach(item => {
        if (item.source) {
          sourceCountMap[item.source] = (sourceCountMap[item.source] || 0) + 1
        }
      })

      return Object.entries(sourceCountMap).map(([source, count]) => ({
        value: source,
        text: `${source} (${count})`,
      }))
    },
    filteredItems() {
      return this.mergedData.filter(item => {
        const matchesSource = this.selectedSources.length === 0
          || this.selectedSources.includes(item.source)
        const matchesSearch = !this.searchQuery
          || (item.request_code
            && item.request_code.toLowerCase().includes(this.searchQuery.toLowerCase()))
        return matchesSource && matchesSearch
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.requests-table-control {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.tabs-row {
  display: flex;
  gap: 0.5rem;
}

.tab-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  border: 1px solid #ebe9f1;
  border-radius: 10px;
  background: #fff;
  color: #5e5873;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #7367f0;
    color: #7367f0;
    background: rgba(115, 103, 240, 0.04);
  }

  &.tab-btn-active {
    background: linear-gradient(135deg, #7367f0 0%, #9e95f5 100%);
    border-color: transparent;
    color: white;
  }
}

.tab-badge {
  padding: 0.2rem 0.5rem;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 700;

  .tab-btn-active & {
    background: rgba(255, 255, 255, 0.3);
  }

  .tab-btn:not(.tab-btn-active) & {
    background: #f5f5f5;
    color: #7367f0;
  }
}

.filters-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: #fafafa;
  border-radius: 12px;
  border: 1px solid #ebe9f1;
}

.filter-group {
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &.filter-search {
    margin-left: auto;
  }
}

.filter-icon {
  color: #7367f0;
}

.filter-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #5e5873;
}

.filter-checkboxes {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.filter-checkbox {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.85rem;
  color: #5e5873;
  cursor: pointer;

  input {
    accent-color: #7367f0;
  }
}

.search-input {
  padding: 0.5rem 0.75rem;
  border: 1px solid #ebe9f1;
  border-radius: 8px;
  font-size: 0.9rem;
  min-width: 220px;

  &:focus {
    outline: none;
    border-color: #7367f0;
    box-shadow: 0 0 0 2px rgba(115, 103, 240, 0.2);
  }
}

.table-wrapper {
  border-radius: 12px;
  border: 1px solid #ebe9f1;
  overflow: hidden;
  background: #fff;
  max-height: min(60vh, 480px);
  display: flex;
  flex-direction: column;
}

.table-scroll {
  overflow: auto;
  flex: 1;
  min-height: 0;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}

.data-table th {
  text-align: left;
  padding: 1rem 1.25rem;
  background: #f8f8f8;
  font-weight: 700;
  color: #5e5873;
  white-space: nowrap;
  position: sticky;
  top: 0;
  z-index: 1;
}

.th-content {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}

.data-table th,
.data-table td {
  padding: 0.85rem 1.25rem;
  border-bottom: 1px solid #f5f5f5;
}

.data-table tbody td {
  padding-top: 3px!important;
  padding-bottom: 3px!important;
}

.data-table tr:last-child td {
  border-bottom: none;
}

.data-table tr:hover td {
  background: #fafafa;
}

.request-link {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  color: #7367f0;
  text-decoration: none;
  font-weight: 600;
  transition: color 0.2s;

  &:hover {
    color: #5e50ee;
  }
}

.status-badge,
.payment-badge,
.source-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.3rem 0.6rem;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 600;
}

.status-badge {
  background: rgba(0, 207, 232, 0.12);
  color: #00cfe8;
}

.payment-paid {
  background: rgba(40, 199, 111, 0.12);
  color: #28c76f;
}

.payment-pending {
  background: rgba(255, 159, 67, 0.12);
  color: #ff9f43;
}

.source-badge {
  background: rgba(115, 103, 240, 0.12);
  color: #7367f0;
}

.empty-state {
  text-align: center;
  padding: 3rem;
  color: #b9b9c3;

  svg {
    margin-bottom: 1rem;
    color: #d8d6de;
  }

  p {
    margin: 0;
    font-size: 0.95rem;
  }
}
</style>
